import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { SourcingService, SourcingSetting } from 'api-clients/monolith';
import { Suspense } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { updateSourcingSettings } from '@/api-clients/update-sourcing-settings';
import { useToaster } from '@/components/base/toaster';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

import { SettingItem } from './setting-item';

const messages = defineMessages({
  funnelAutomationTitle: {
    defaultMessage: 'Funnel Automations',
    description: 'Title for funnel automations setting',
  },
  funnelAutomationDescription: {
    defaultMessage:
      'Automatically pauses sourcing when there are enough qualified candidates moving through the hiring stages to meet the hiring goal.',
    description: 'Description of funnel automations',
  },
  hiringAutomationTitle: {
    defaultMessage: 'Hiring Automations',
    description: 'Title for hiring automations setting',
  },
  hiringAutomationDescription: {
    defaultMessage:
      'Automatically pauses sourcing when the hiring goal period end date is reached.',
    description: 'Description of hiring automations',
  },
  budgetAutomationTitle: {
    defaultMessage: 'Budget Automations',
    description: 'Title for budget automations setting',
  },
  budgetAutomationDescription: {
    defaultMessage:
      'Automatically pauses sourcing once opening spend reaches its target budget. Set a target budget in opening details to enable this.',
    description: 'Description of budget automations',
  },
});

const automationSettings: Array<{
  key: keyof SourcingSetting;
  titleMessage: keyof typeof messages;
  descriptionMessage: keyof typeof messages;
}> = [
  {
    key: 'funnel_automation_enabled',
    titleMessage: 'funnelAutomationTitle',
    descriptionMessage: 'funnelAutomationDescription',
  },
  {
    key: 'hiring_automation_enabled',
    titleMessage: 'hiringAutomationTitle',
    descriptionMessage: 'hiringAutomationDescription',
  },
  {
    key: 'budget_automation_enabled',
    titleMessage: 'budgetAutomationTitle',
    descriptionMessage: 'budgetAutomationDescription',
  },
];

interface SettingsPageContentProps {
  settings?: SourcingSetting;
  onSettingChange?: (key: keyof SourcingSetting, checked: boolean) => void;
  disabled?: boolean;
}

function AutomationSettingsContent({
  settings,
  onSettingChange,
  disabled = false,
}: SettingsPageContentProps) {
  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Typography variant="h5" as="h2">
            <FormattedMessage
              defaultMessage="Enable Automations for All Openings"
              description="Main section title for enabling automations"
            />
          </Typography>
          <Typography variant="body1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Automations are designed to minimize wasted spend by automatically pausing sourcing once hiring goals or budget limits are reached. Review each automation below prior to enabling."
              description="Description explaining how automations help cut wasted spend"
            />
          </Typography>
        </div>

        <div className="flex flex-col gap-6">
          {automationSettings.map(setting => (
            <SettingItem
              key={setting.key}
              title={<FormattedMessage {...messages[setting.titleMessage]} />}
              description={
                <FormattedMessage {...messages[setting.descriptionMessage]} />
              }
              checked={settings?.[setting.key] ?? false}
              onCheckedChange={checked =>
                onSettingChange?.(setting.key, checked)
              }
              disabled={disabled}
              isLoading={!settings}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AutomationSettingsWrapper() {
  const intl = useIntl();
  const { notifySuccess, notifyError } = useToaster();
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery<SourcingSetting>({
    queryKey: queryKeys.settings.automations,
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingSettings() as Promise<SourcingSetting>,
  });

  const settings: SourcingSetting = data;

  const { mutate: updateSettings, isPending } = useMutation({
    mutationFn: updateSourcingSettings,
    onMutate: variables => {
      const previousSettings = queryClient.getQueryData<SourcingSetting>(
        queryKeys.settings.automations,
      );
      const optimisticSettings: SourcingSetting = {
        ...settings,
        ...variables,
      };
      queryClient.setQueryData<SourcingSetting>(
        queryKeys.settings.automations,
        optimisticSettings,
      );
      return { previousSettings };
    },
    onSuccess: response => {
      queryClient.setQueryData<SourcingSetting>(
        queryKeys.settings.automations,
        response,
      );
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Settings updated successfully!',
          description: 'Success message for updating sourcing settings',
        }),
      });
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSettings) {
        const { previousSettings } = context;
        queryClient.setQueryData<SourcingSetting>(
          ['sourcingSettings'],
          previousSettings,
        );
      }
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update settings!',
          description: 'Error message for updating sourcing settings',
        }),
      });
    },
  });

  const handleSettingChange = (
    key: keyof SourcingSetting,
    checked: boolean,
  ) => {
    updateSettings({
      [key]: checked,
    });
  };

  return (
    <AutomationSettingsContent
      settings={settings}
      onSettingChange={handleSettingChange}
      disabled={isPending}
    />
  );
}

export function AutomationSettings() {
  return (
    <Suspense fallback={<AutomationSettingsContent />}>
      <AutomationSettingsWrapper />
    </Suspense>
  );
}
